import type { UsageQuery } from "../adapter";
import { apiBaseUrl, type QueryParams } from "../http";
import type { WorkspaceMember } from "../types";

export { apiBaseUrl };

export function asObject(value: unknown): Record<string, unknown> {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return {};
}

export function toStringRecord(value: unknown): Record<string, string> {
	const source = asObject(value);
	return Object.fromEntries(
		Object.entries(source).map(([key, entry]) => [
			key,
			typeof entry === "string" ? entry : JSON.stringify(entry),
		]),
	);
}

export function toNumber(value: unknown, fallback = 0): number {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}
	if (typeof value === "string") {
		const parsed = Number(value);
		if (Number.isFinite(parsed)) {
			return parsed;
		}
	}
	return fallback;
}

export function toRole(value: string | undefined | null): WorkspaceMember["role"] {
	const normalized = (value ?? "").split(",")[0]?.trim();
	if (normalized === "owner" || normalized === "admin") {
		return normalized;
	}
	return "member";
}

export function toDateOnly(value: string): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}
	return date.toISOString().slice(0, 10);
}

export function toIsoDateTime(
	value: string | undefined,
	boundary: "start" | "end",
): string | undefined {
	if (!value) {
		return undefined;
	}

	if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
		const suffix = boundary === "start" ? "T00:00:00.000Z" : "T23:59:59.999Z";
		return new Date(`${value}${suffix}`).toISOString();
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return undefined;
	}
	return parsed.toISOString();
}

export function usageQueryParams(query: UsageQuery): QueryParams {
	return {
		from: toIsoDateTime(query.from, "start"),
		to: toIsoDateTime(query.to, "end"),
		granularity: query.granularity,
	};
}
